import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import type { PlantDowntimeItem } from "../../types";
import { formatSecondsToDowntime } from "../../utils/timeFormat";

export type DowntimeEquipmentChartProps = {
  title: string;
  color: string;
  data: PlantDowntimeItem[];
  averageDowntimeFormatted: string;
};

function formatYAxisSeconds(seconds: number): string {
  if (seconds === 0) return "0h";
  const hours = seconds / 3600;
  if (hours < 1) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  }
  if (hours >= 100) {
    return `${Math.floor(hours)}h`;
  }
  return `${hours.toFixed(1)}h`;
}

export default function DowntimeEquipmentChart({
  title,
  color,
  data,
  averageDowntimeFormatted,
}: DowntimeEquipmentChartProps) {
  const { theme } = useContext(MainContext);

  const xLabels = data.map((item) => item.plantName);
  const seriesData = data.map((item) => ({
    value: item.downtimeSeconds,
    itemStyle: { color },
  }));

  const option: EChartsOption = {
    backgroundColor: "transparent",
    grid: {
      left: "8%",
      right: "5%",
      bottom: "8%",
      top: "12%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: xLabels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: theme.secondaryTextColor || "#999",
        fontSize: 11,
        margin: 10,
        interval: 0,
        rotate: xLabels.length > 4 ? 15 : 0,
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.cardBorderColor || "#333",
          opacity: 0.35,
        },
      },
      axisLabel: {
        color: theme.secondaryTextColor || "#999",
        fontSize: 11,
        formatter: (v: number) => formatYAxisSeconds(v),
      },
    },
    series: [
      {
        name: title,
        type: "bar",
        data: seriesData,
        barWidth: xLabels.length <= 2 ? "35%" : "55%",
        itemStyle: { color },
      },
    ],
    tooltip: {
      trigger: "axis",
      backgroundColor: theme.backgroundColor || "#1a1a1a",
      borderColor: theme.cardBorderColor || "#444",
      borderWidth: 1,
      textStyle: {
        color: theme.primaryTextColor || "#fff",
        fontSize: 12,
      },
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: "rgba(150, 150, 150, 0.1)",
        },
      },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return "";
        const first = params[0] as {
          axisValue?: string;
          dataIndex?: number;
          value?: number;
        };
        const plantName = first.axisValue ?? "";
        const idx = first.dataIndex ?? 0;
        const item = data[idx];
        const formatted =
          item?.formattedDowntime ??
          formatSecondsToDowntime(
            typeof first.value === "number" ? first.value : 0,
          );

        return `<strong>${plantName}</strong><br/>
<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background-color:${color};margin-right:6px;"></span>
${title}: <strong>${formatted}</strong>`;
      },
    },
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#202020b0",
        borderRadius: "6px",
        border: `1px solid ${theme.cardBorderColor || "#334155"}`,
        padding: 1.5,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 600,
          color: theme.primaryTextColor,
          marginBottom: 1,
          textAlign: "left",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: 280 }}>
        <RopChart option={option} />
      </Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: theme.secondaryTextColor,
          marginTop: 1,
          textAlign: "center",
        }}
      >
        Média de indisponibilidade:{" "}
        <Box component="span" sx={{ color: theme.primaryTextColor, fontWeight: 600 }}>
          {averageDowntimeFormatted}
        </Box>
      </Typography>
    </Box>
  );
}
